// Map module contract (§18.3, §19.1). Frozen after WP0: additive changes only.
import type { MapId } from '../ids';
import type { Vec2, World } from '../types';
import type { RoundClock } from './clock';

export const SolidKind = { CIRCLE: 0, CAPSULE: 1 } as const;
export type SolidKind = (typeof SolidKind)[keyof typeof SolidKind];

/** `Solid.tag`: hazard response selector for `MapDef.onSolidContact`. */
export const SolidTag = { NONE: 0, BUMPER: 1, FLIPPER: 2, SWEEPER: 3, PINBALL: 4, SLINGSHOT: 5, CUP: 6 } as const;
export type SolidTag = (typeof SolidTag)[keyof typeof SolidTag];

export interface Solid {
  kind: 0 | 1;           // 0 circle, 1 capsule
  ax: number; ay: number; bx: number; by: number;  // circle uses (ax, ay)
  r: number; top: number; restitution: number;
  vx: number; vy: number; omega: number; cx: number; cy: number;  // kinematic: linear vel or rotation about (cx,cy)
  tag: number;           // hazard tag for special response (0 none, 1 bumper, 2 flipper, 3 sweeper, 4 pinball, 5 slingshot, 6 cup)
  index: number;
}

export type VoidKind = 'tea' | 'grid' | 'milk' | 'foam' | 'lilac';

export interface MapDef {
  id: MapId;
  name: string;                              // 'Tipsy Teacup'
  tpb: number; tpbSD: number;                // ticks per beat (normal / sudden death)
  bounds: { minX: number; maxX: number; minY: number; maxY: number };   // camera + ghost bounds
  spawns: Vec2[];                            // 8
  voidKind: VoidKind;
  center: Vec2;                              // crown spawn / bot centroid fallback
  initState(w: World): void;                 // fill MapState, spawn CUBE/DUMMY entities
  groundAt(w: World, x: number, y: number): number;            // height or VOID
  nearestGround(w: World, x: number, y: number, out: GroundHit): GroundHit;
  solids(w: World, out: Solid[]): number;    // fills out[], returns count (reuse array; no allocation per tick)
  onSolidContact(w: World, bodySlotOrEnt: number, isEntity: boolean, s: Solid, nx: number, ny: number): void; // hazard response
  step(w: World, clock: RoundClock): void;   // stateful map logic + non-solid hazards (flames, bubbles, tilt slide)
  slideAccel?(w: World, x: number, y: number, out: Vec2): void;          // teacup
  danger(w: World, x: number, y: number, aheadTicks: number): number;    // 0..1 for bots (pure; reads state)
  hazardPose(w: World, tick: number, out: HazardPose): HazardPose;       // for renderer: arm angles, flipper angles, ball positions, bubble positions, cup angle
  crateExclusion(w: World, x: number, y: number): boolean;               // true if crates must not land here
}
export interface GroundHit { x: number; y: number; nx: number; ny: number; dist: number }
export interface HazardPose {
  armAngles: number[]; armCount: number; armDir: number;     // spin
  flipperAngles: [number, number];                            // pinball (radians from rest)
  balls: { x: number; y: number; active: boolean }[];         // pinball (2)
  bubbles: { x: number; y: number; scale: number; wobble: number }[];   // spin (6)
  cupAngle: number;                                           // teacup
  candleFlicker: number[]; candleFlame: number[];             // cake (6 each, 0..1)

  // ---- Additive (WP2): telegraph timing the renderer, audio and bots key off. `createHazardPose`
  // allocates every field, and every map writes all of them (neutral values when not its map).
  /** Spin: signed angular speed of the arms in rad/s (+ = increasing angle); hum pitch, bristle bend. */
  armOmega?: number;
  /**
   * Spin: the Sudden Death third arm's drop, 0 → 1 over its 1-bar shadow (the last Ramp bar), 1
   * once it has landed on the Sudden Death downbeat. `armCount` already counts it (3) while it
   * drops; it only sweeps once landed. 0 while it has not appeared.
   */
  armDrop?: number;
  /** Spin: 0..1 progress of the 2-beat reversal warning (buzzer, red dome lights); 0 otherwise. */
  reverseWarn?: number;
  /** Pinball: 0..1 progress of the 2-beat pre-fire telegraph; 0 otherwise. */
  flipperWarn?: number;
  /** Pinball: the blink of the flipper lights (lit on the first half of each telegraph beat). */
  flipperLights?: boolean;
  /** Pinball: 0..1 progress of a 1-bar BALL LAUNCH warning (lane lights chase); 0 otherwise. */
  ballWarn?: number;
  /** Pinball: per ball, the distance it has rolled along its loop (m), for the rolling spin. */
  ballRoll?: number[];
  /** Teacup: per wedge (index k at angle k·45°), 0..1 progress of its 1-bar warning (crack spread, shake); 1 once fallen. */
  wedgeCrack?: number[];
  /** Teacup: per wedge, seconds since it fell (−1 while attached), for the tip-and-drop animation. */
  wedgeFallSec?: number[];
  /**
   * Cake: the tile the Sudden Death crumble cracks on the next beat (−1 for none) and 0..1 of its
   * 1-beat highlight. Cracking, falling, gone and regrowing tiles are in `MapState.tileState` /
   * `tileTimer` (ticks left in the state).
   */
  sdNextTile?: number;
  sdNextWarn?: number;
  /** Every hazard inside its telegraph window at this tick (the first `telegraphCount` entries). */
  telegraphs?: HazardInfo[];
  telegraphCount?: number;
}

/**
 * One hazard telegraph (additive, WP2; named in §15). Every telegraph lasts at least 36 ticks
 * (0.6 s, §6): `fireTick − warnTick ≥ 36`. The same moments are emitted by the map step as
 * HAZARD_WARN (d = lead ticks, from the warning to the moment it hits) and HAZARD_FIRE events,
 * which clients derive from fresh prediction steps (§20.10). Cake's TILE_SD telegraph ends when the
 * tile falls (one beat of highlight, then 24 ticks of cracking); its fall shows in `tileState`.
 */
export interface HazardInfo {
  /** `HazardKind`. */
  kind: number;
  /** Per-kind index: flipper 0, ball 0/1, wedge 0–7, candle 0–5, third arm 2, reversal number (1, 2, …), bubble 0–5, tile 0–36. */
  index: number;
  /** Round tick the telegraph starts. */
  warnTick: number;
  /** Round tick the hazard fires. */
  fireTick: number;
  /** Where it fires (meters), for positional audio and VFX. */
  x: number;
  y: number;
}

/** Fixed HazardPose array lengths: sweeper arms (incl. the SD third arm), pinballs, bubbles, candles. */
export const POSE_ARMS = 3;
export const POSE_BALLS = 2;
export const POSE_BUBBLES = 6;
export const POSE_CANDLES = 6;
/** Additive (WP2): Teacup wedges and the telegraph list capacity. */
export const POSE_WEDGES = 8;
export const POSE_TELEGRAPHS = 16;

export function createHazardInfo(): HazardInfo {
  return { kind: 0, index: 0, warnTick: 0, fireTick: 0, x: 0, y: 0 };
}

export function createSolid(): Solid {
  return { kind: 0, ax: 0, ay: 0, bx: 0, by: 0, r: 0, top: 0, restitution: 0, vx: 0, vy: 0, omega: 0, cx: 0, cy: 0, tag: 0, index: 0 };
}

export function createGroundHit(): GroundHit {
  return { x: 0, y: 0, nx: 0, ny: 0, dist: 0 };
}

/** A neutral pose with every array at its fixed length (maps overwrite in place). */
export function createHazardPose(): HazardPose {
  return {
    armAngles: new Array<number>(POSE_ARMS).fill(0), armCount: 0, armDir: 1,
    flipperAngles: [0, 0],
    balls: Array.from({ length: POSE_BALLS }, () => ({ x: 0, y: 0, active: false })),
    bubbles: Array.from({ length: POSE_BUBBLES }, () => ({ x: 0, y: 0, scale: 0, wobble: 0 })),
    cupAngle: 0,
    candleFlicker: new Array<number>(POSE_CANDLES).fill(0),
    candleFlame: new Array<number>(POSE_CANDLES).fill(0),
    armOmega: 0, armDrop: 0, reverseWarn: 0,
    flipperWarn: 0, flipperLights: false, ballWarn: 0,
    ballRoll: new Array<number>(POSE_BALLS).fill(0),
    wedgeCrack: new Array<number>(POSE_WEDGES).fill(0),
    wedgeFallSec: new Array<number>(POSE_WEDGES).fill(-1),
    sdNextTile: -1, sdNextWarn: 0,
    telegraphs: Array.from({ length: POSE_TELEGRAPHS }, createHazardInfo),
    telegraphCount: 0,
  };
}
